import React from 'react'
import { useDispatch } from 'react-redux'
import { CustomInput } from 'reactstrap'
import { v4 } from 'uuid'
import { getCurrentBuild } from 'web/data/core-data'
import { BuildData, FightPlayer, RosterPlayer } from 'web/data/models'
import { EditSetupActions, useEditSetupState } from 'web/redux/slices/edit-setup-slice'
import { useRosterSelectors, useRosterState } from 'web/redux/slices/roster-slice'
import { areArraysEmpty, assertDefined, expectOneById } from 'web/utils/utils'

export const FromRosterSelectBox: React.FC = () => {

    const disp = useDispatch()
    const buildData = getCurrentBuild()

    const rosterState = useRosterState()
    const editSetupState = useEditSetupState()

    const setup = editSetupState.setup
    assertDefined(setup)

    const rosterSelectors = useRosterSelectors()

    const addManyPlayers = (players: FightPlayer[]) => players.forEach(player => {
        disp(EditSetupActions.addPlayer({
            player: {
                ...player,
                id: v4()
            }
        }))
    })

    // Filter out players of the same name and spec that have already
    // been added
    const playersByRole = rosterSelectors.getPlayersByRole(rosterState.players.filter(player => {
        return setup.fightPlayers.some(fp => {
            return (
                fp.name === player.name &&
                fp.specId === player.specId
            )
        }) === false
    }))

    if (rosterState.players.length <= 0) {

        return (
            <div className="align-items-center border rounded d-flex justify-content-center p-2 small text-muted">
                {"No players added to roster yet"}
            </div>
        )

    } else {

        const handleSelect = (e: React.ChangeEvent<HTMLInputElement>): void => {
            const playerId = e.target.value

            if (playerId.startsWith("all-")) {

                if (playerId === "all-healers") {
                    addManyPlayers(playersByRole.healers)
                }
                if (playerId === "all-tanks") {
                    addManyPlayers(playersByRole.tanks)
                }
                if (playerId === "all-melee") {
                    addManyPlayers(playersByRole.melee)
                }
                if (playerId === "all-ranged") {
                    addManyPlayers(playersByRole.ranged)
                }

                if (playerId === "all-players") {
                    addManyPlayers([
                        ...playersByRole.healers,
                        ...playersByRole.tanks,
                        ...playersByRole.melee,
                        ...playersByRole.ranged,
                    ])
                }

                return
            }

            if (playerId === "") {
                return
            }

            const player = expectOneById(rosterState.players, playerId)

            disp(EditSetupActions.addPlayer({
                player: {
                    ...player,
                    id: v4()
                }
            }))
        }

        const allPlayersAdded = areArraysEmpty(Object.values(playersByRole))

        return (
            <CustomInput
                id="player-select"
                bsSize="sm"
                type="select"
                value={""}
                onChange={handleSelect}
                disabled={allPlayersAdded}
            >

                {allPlayersAdded &&
                    <option value="">{"All players have been added"}</option>
                }

                {!allPlayersAdded && (
                    <>
                        <option value="">{"-- Select player --"}</option>

                        {playersByRole.tanks.length >= 1 && makeOptGroup(buildData, "Tanks", playersByRole.tanks)}
                        {playersByRole.healers.length >= 1 && makeOptGroup(buildData, "Healers", playersByRole.healers)}
                        {playersByRole.melee.length >= 1 && makeOptGroup(buildData, "Melee", playersByRole.melee)}
                        {playersByRole.ranged.length >= 1 && makeOptGroup(buildData, "Ranged", playersByRole.ranged)}

                        <optgroup label="Custom">
                            <option value="all-players">{"All roster players"}</option>

                            {playersByRole.tanks.length >= 1 &&
                                <option value="all-tanks">{"All tanks"}</option>
                            }
                            {playersByRole.healers.length >= 1 &&
                                <option value="all-healers">{"All healers"}</option>
                            }
                            {playersByRole.melee.length >= 1 &&
                                <option value="all-melee">{"All melee"}</option>
                            }
                            {playersByRole.ranged.length >= 1 &&
                                <option value="all-ranged">{"All ranged"}</option>
                            }
                        </optgroup>
                    </>
                )}


            </CustomInput>
        )

    }
}

function makeOptGroup(buildData: BuildData, label: string, items: RosterPlayer[]): JSX.Element {
    return (
        <optgroup label={label}>
            {items.map(item => (
                <option key={item.id} value={item.id}>
                    {item.name} {`(${buildData.Spec(item.specId).shortName})`}
                </option>
            ))}
        </optgroup>
    )
}

